import { ApiResponse } from "../context/VolunteersHighlightContext/constants";
import {
	KvkkFormData,
	KvkkGetApiResponse,
	KvkkListApiResponse,
	UpdateKvkkRequest,
} from "../context/KvkkContext/constants";
import { axiosInstance } from "../utils/axiosInstance";
import { getRichTextPlainText } from "../utils/richText";

const ENDPOINTS = {
	KVKK: "/cms/kvkk",
};

const trimText = (value?: string) => (value === undefined ? undefined : value.trim());

// Only the cultures the user filled in are sent; the other one stays without a text.
export const buildAddKvkkRequest = (data: KvkkFormData) => ({
	isManager: data.isManager,
	isPage: data.isPage,
	platform: data.platform,
	appId: data.appId,
	kvkkTextLocalizable: data.kvkkTextLocalizable
		.filter(item => item.title.trim() || getRichTextPlainText(item.text))
		.map(item => ({
			culture: item.culture,
			title: item.title.trim(),
			text: item.text.trim(),
		})),
});

export const buildUpdateKvkkRequest = (request: UpdateKvkkRequest): UpdateKvkkRequest => ({
	...request,
	...(request.kvkkTextLocalizable
		? {
			kvkkTextLocalizable: request.kvkkTextLocalizable.map(item => ({
				...item,
				title: trimText(item.title),
				text: trimText(item.text),
			})),
		}
		: {}),
});

const getEndpoint = (kvkkTextId: string) => `${ENDPOINTS.KVKK}/${encodeURIComponent(kvkkTextId)}`;

export class KvkkService {
	static async getKvkkList(): Promise<KvkkListApiResponse> {
		const response = await axiosInstance.get(ENDPOINTS.KVKK);
		return response.data;
	}

	static async getKvkkDetails(kvkkTextId: string): Promise<KvkkGetApiResponse> {
		const response = await axiosInstance.get(getEndpoint(kvkkTextId));
		return response.data;
	}

	static async addKvkk(data: KvkkFormData): Promise<ApiResponse<unknown>> {
		const response = await axiosInstance.post(ENDPOINTS.KVKK, buildAddKvkkRequest(data));
		return response.data;
	}

	static async updateKvkk(kvkkTextId: string, request: UpdateKvkkRequest): Promise<ApiResponse<unknown>> {
		const response = await axiosInstance.put(getEndpoint(kvkkTextId), buildUpdateKvkkRequest(request));
		return response.data;
	}

	// The backend answers a deletion with 204 and no body.
	static async removeKvkk(kvkkTextId: string): Promise<ApiResponse<unknown>> {
		const response = await axiosInstance.delete(getEndpoint(kvkkTextId));

		if (response.status === 204) {
			return { result: null, error: null, isSuccessful: true };
		}

		return response.data;
	}
}
